import type { Product } from "../product-types";
import type { PublicProductDto } from "./contracts";

const availabilityCopy = {
  in_stock: "有货",
  low_stock: "少量",
  unavailable: "暂缺",
  unknown: "库存待确认",
} as const;

const previewImageProxy = import.meta.env.DEV && import.meta.env.VITE_BEAUTINOW_PREVIEW_IMAGE_PROXY === "true";

function displayImageUrl(value: string | null | undefined): string {
  const source = String(value || "");
  if (!previewImageProxy || !source) return source;
  try {
    const url = new URL(source);
    if (url.protocol !== "https:") return source;
    if (["image-c.weimobwmc.com", "img.xssdcdn.com"].includes(url.hostname)) {
      return `http://127.0.0.1:43175/prod-image?url=${encodeURIComponent(source)}`;
    }
  } catch {
    return source;
  }
  return source;
}

export function toProduct(row: PublicProductDto): Product {
  const brand = row.brand?.verified ? (row.brand.name || row.brand.nameEn || row.brand.nameCn || "") : "";
  return {
    id: row.productKey,
    saleStatus: row.saleStatus || undefined,
    salesCheckedAt: row.salesCheckedAt || undefined,
    exactMatch: row.exactMatch,
    defaultVisible: row.defaultVisible,
    brand,
    brandNameEn: row.brand?.verified ? row.brand.nameEn : "",
    brandNameCn: row.brand?.verified ? row.brand.nameCn : "",
    displayBrand: row.brand?.verified ? [row.brand.nameEn || row.brand.name, row.brand.nameCn].filter(Boolean).join(" ") : "",
    brandVerified: Boolean(row.brand?.verified),
    name: row.nameEn?.trim() || row.nameCn?.trim() || "",
    chineseName: row.nameEn?.trim() && row.nameCn?.trim() !== row.nameEn.trim() ? row.nameCn : "",
    image: displayImageUrl(row.imageUrl),
    imageUrls: (Array.isArray(row.imageUrls) ? row.imageUrls : [row.imageUrl].filter(Boolean)).map(displayImageUrl),
    imageSource: row.imageSource || "",
    tags: Array.isArray(row.tags) ? row.tags : [],
    reason: row.reason || "",
    description: row.description || "",
    isListed: true,
    hasStock: row.saleStatus === "available",
    recommendable: Boolean(row.recommendable),
    entryKind: row.entryKind,
    sourceOnly: row.sourceOnly,
    concentration: row.concentration,
    productType: "perfume",
    listedAt: row.listedAt,
    productUrl: row.purchaseUrl || undefined,
    miniAppId: row.miniAppId || undefined,
    miniProgramUrl: row.miniProgramUrl || undefined,
    gender: row.gender,
    fragranceType: row.fragranceType,
    matchReasons: row.matchReasons,
    reviewInsights: row.reviewInsights,
    notePyramid: row.notePyramid,
    scentNotes: row.scentNotes,
    skus: (row.skuOptions || []).map((sku) => ({
      id: sku.skuKey,
      packageType: sku.concentration || "香水",
      concentration: sku.concentration || "香水",
      packaging: sku.packaging || "正装",
      capacity: sku.capacity || "规格以商品页为准",
      stock: availabilityCopy[sku.availability] || "暂缺",
      availability: sku.availability,
      saleStatus: sku.saleStatus || undefined,
      salesCheckedAt: sku.salesCheckedAt || undefined,
      purchaseUrl: sku.purchaseUrl || undefined,
      miniAppId: sku.miniAppId || row.miniAppId || undefined,
      miniProgramUrl: sku.miniProgramUrl || row.miniProgramUrl || undefined,
      image: displayImageUrl(sku.imageUrl || row.imageUrl),
    })),
  };
}

export function toProducts(rows: PublicProductDto[] | undefined): Product[] {
  if (!Array.isArray(rows)) return [];
  return rows.map(toProduct).filter((row) => row.id && row.name);
}
